import { PageHero } from "@/components/PageHero";
import { PageSkeleton } from "@/components/Skeletons";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, getToken, getSessionId } from "@/lib/api";
import { toast } from "sonner";
import { track, trackOnce, EVENTS } from "@/lib/analytics";
import ShareUpdateDialog from "@/components/ShareUpdateDialog";
import {
    FileText, Download, Printer, Share2, Mail, Loader2,
    AlertCircle, Sparkles, ArrowLeft, ChevronRight, Stethoscope,
    User, Heart, Brain, Pill, ClipboardList, MessageCircle, FileCheck2,
} from "lucide-react";

/**
 * /care-summary — caregiver-facing preview of the Care Summary PDF.
 *
 * Pulls structured data from `/api/care-summary/{sid}/data` and renders a
 * "this is what your doctor will see" preview. One click downloads the
 * server-rendered PDF; another mails the link to a care-team email.
 *
 * Empty sections (no behaviors logged, no appointment prep, etc.) collapse
 * automatically so a freshly-onboarded caregiver doesn't see a sea of
 * "nothing here yet" — they see a useful starting point with one clear
 * next action ("Log your first behavior" / "Prep your first appointment").
 */
export const RANGES = [
    { key: "all", label: "Everything" },
    { key: "12m", label: "Last 12 months" },
    { key: "6m", label: "Last 6 months" },
    { key: "3m", label: "Last 3 months" },
    { key: "1m", label: "Last month" },
];

const VISIT_TYPES = [
    { key: "neurology", label: "Neurology / memory clinic" },
    { key: "primary_care", label: "Primary care" },
    { key: "er", label: "ER / urgent" },
    { key: "new_specialist", label: "New specialist" },
    { key: "insurance_legal", label: "Insurance / legal" },
];

export default function CareSummary() {
    const [state, setState] = useState({ loading: true, error: null, data: null });
    const [sel, setSel] = useState(new Set());
    const [range, setRange] = useState("all");
    const [docSel, setDocSel] = useState(new Set());
    const [visitType, setVisitType] = useState(null);
    const [laneNote, setLaneNote] = useState("");
    const [reasons, setReasons] = useState({});
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState("");
    const [share, setShare] = useState(null);
    const sid = getSessionId();

    useEffect(() => {
        if (!sid) { setState({ loading: false, error: "No session found.", data: null }); return; }
        api.get(`/care-summary/${sid}/data`)
            .then((r) => {
                const d = r.data || {};
                setState({ loading: false, error: null, data: d });
                const avail = availableSections(d).filter(s => s.count > 0).map(s => s.key);
                setSel(new Set(avail));
                setDocSel(new Set(((d.journey || {}).documents || []).map(x => x.id)));
                trackOnce("care_summary_generated", EVENTS.CARE_SUMMARY_GENERATED, {});
            })
            .catch((e) => setState({ loading: false, error: e?.response?.data?.detail || "Could not load summary.", data: null }));
    }, [sid]);

    const toggle = (key) => setSel(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });
    const toggleDoc = (id) => setDocSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

    const askLane = async (vt) => {
        setVisitType(vt);
        setBusy("lane");
        try {
            const r = await api.post("/care-share/suggest", { session_id: sid, visit_type: vt });
            const d = state.data || {};
            const availKeys = new Set(availableSections(d).filter(s => s.count > 0).map(s => s.key));
            setSel(new Set((r.data.sections || []).filter(k => availKeys.has(k))));
            setRange(r.data.range || "all");
            setReasons(r.data.reasons || {});
            setLaneNote(r.data.note || "");
            track("care_share_lane_suggest", { visit_type: vt });
        } catch { toast.error("Couldn't reach Lane — pick sections yourself below."); }
        finally { setBusy(""); }
    };

    const payload = () => ({
        session_id: sid,
        sections: [...sel],
        range,
        document_ids: [...docSel],
        visit_type: visitType,
        title: visitType ? `${(VISIT_TYPES.find(v => v.key === visitType) || {}).label} summary` : "Care Summary",
    });

    const createShare = async (withEmail) => {
        if (sel.size === 0) { toast.error("Pick at least one section to share."); return; }
        setBusy(withEmail ? "email" : "link");
        try {
            const body = payload();
            if (withEmail) {
                if (!/.+@.+\..+/.test(email)) { toast.error("Enter a valid email address."); setBusy(""); return; }
                body.recipient_email = email.trim();
            }
            const r = await api.post("/care-share/create", body);
            setShare(r.data);
            track(EVENTS.CARE_SUMMARY_SHARED, { method: withEmail ? "email" : "link", sections: [...sel].join(",") });
            if (withEmail) {
                toast.success(r.data.emailed?.sent ? `Sent to ${email}.` : "Link created (email couldn't send — copy it below).");
            } else {
                toast.success("Share link created.");
            }
        } catch (e) { toast.error(e?.response?.data?.detail || "Could not create share."); }
        finally { setBusy(""); }
    };

    const openPdf = async () => {
        if (sel.size === 0) { toast.error("Pick at least one section first."); return; }
        setBusy("pdf");
        try {
            const r = share || (await api.post("/care-share/create", payload())).data;
            if (!share) setShare(r);
            window.open(`${API}/care-share/${r.token}/pdf`, "_blank");
            track(EVENTS.CARE_SUMMARY_SHARED, { method: "pdf" });
        } catch { toast.error("Could not build the PDF."); }
        finally { setBusy(""); }
    };

    const copyLink = () => { if (share?.url) { navigator.clipboard.writeText(share.url); toast.success("Link copied."); } };

    if (state.loading) return <Shell><PageSkeleton cards={3} testid="care-summary-loading" /></Shell>;
    if (state.error) return (
        <Shell><div className="ml-card p-6" data-testid="care-summary-error">
            <p className="flex items-center gap-2 text-coral-dark"><AlertCircle size={18} /> {state.error}</p>
        </div></Shell>
    );

    const d = state.data || {};
    const profile = d.patient_profile || {};
    const meds = (d.medication_changes?.medications?.length ? d.medication_changes.medications : profile.medications) || [];
    const team = profile.healthcare_team || [];
    const services = d.services || [];
    const behaviors = d.behaviors || [];
    const appt = d.appointment;
    const journey = d.journey || {};
    const docs = journey.documents || [];
    const sections = availableSections(d);
    const first = (d.loved_one_name || "").split(" ")[0];

    return (
        <Shell>
            <div data-clarity-mask="true">
            <div className="flex items-center gap-1.5 text-sm mb-3" data-testid="care-summary-breadcrumb">
                <Link to="/dashboard" className="text-[#4C5551] hover:text-teal-dark inline-flex items-center gap-1"><ArrowLeft size={14} /> Dashboard</Link>
                <ChevronRight size={12} className="text-[#6E7A74]" />
                <span className="text-[#6E7A74]">Care Summary</span>
            </div>
            <PageHero
                testId="care-summary-header"
                kicker="One place to share"
                title="Care Summary"
                subtitle={<>Build exactly what you want to hand off — for a neurologist, the ER, a new specialist, insurance, or family. Everything you&apos;ve added or imported lives here; pick what to include and share it as a link, PDF, or email. No re-typing.</>}
            />

            {/* Lane visit-prep */}
            <div className="ml-card p-5 mt-5 bg-teal-soft/30" data-testid="care-summary-lane-prep">
                <div className="flex items-center gap-2 mb-1.5">
                    <span className="rounded-full bg-teal-dark text-white p-1.5"><Sparkles size={14} /></span>
                    <h2 className="font-heading text-base font-semibold text-[#1F2624]">Lane, help me prepare</h2>
                </div>
                <p className="text-sm text-[#4C5551] mb-3">What kind of visit is this? I&apos;ll pre-check what usually matters — you can adjust anything.</p>
                <div className="flex flex-wrap gap-2" data-testid="visit-type-chips">
                    {VISIT_TYPES.map(v => (
                        <button key={v.key} onClick={() => askLane(v.key)} data-testid={`visit-type-${v.key}`}
                            className={`rounded-2xl px-4 py-2 text-sm font-medium border transition-colors ${visitType === v.key ? "bg-teal-dark text-white border-teal-dark" : "bg-white text-[#4C5551] border-cream-dark hover:border-teal"}`}>
                            {v.label}
                        </button>
                    ))}
                    {busy === "lane" && <Loader2 size={16} className="animate-spin text-teal-dark self-center" />}
                </div>
                {laneNote && <p className="mt-3 text-sm text-teal-dark bg-white rounded-2xl px-4 py-3" data-testid="lane-note">{laneNote}</p>}
            </div>

            {/* Section picker + range */}
            <div className="ml-card p-5 mt-4" data-testid="care-summary-builder">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h2 className="font-heading text-base font-semibold text-[#1F2624]">What to include</h2>
                    <label className="text-sm text-[#4C5551] flex items-center gap-2">
                        Time range
                        <select value={range} onChange={(e) => setRange(e.target.value)} data-testid="range-select"
                            className="rounded-lg border border-cream-dark px-2.5 py-1.5 text-sm">
                            {RANGES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                        </select>
                    </label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="section-checklist">
                    {sections.map(s => (
                        <label key={s.key} data-testid={`section-${s.key}`}
                            className={`flex items-start gap-3 rounded-2xl border px-4 py-3 cursor-pointer transition-colors ${s.count === 0 ? "opacity-50 cursor-not-allowed border-cream-dark" : sel.has(s.key) ? "border-teal-dark bg-teal-soft/30" : "border-cream-dark hover:border-teal"}`}>
                            <input type="checkbox" disabled={s.count === 0} checked={sel.has(s.key)} onChange={() => toggle(s.key)}
                                data-testid={`section-check-${s.key}`} className="mt-1 accent-teal-dark" />
                            <span className="min-w-0">
                                <span className="text-sm font-medium text-[#1F2624]">{s.label}</span>
                                <span className="text-xs text-[#6E7A74]"> · {s.count === 0 ? "nothing yet" : s.hint}</span>
                                {reasons[s.key] && <span className="block text-xs text-teal-dark mt-0.5">{reasons[s.key]}</span>}
                            </span>
                        </label>
                    ))}
                </div>

                {sel.has("documents") && docs.length > 0 && (
                    <div className="mt-4 rounded-2xl border border-cream-dark p-4" data-testid="document-picker">
                        <p className="text-xs uppercase tracking-wider font-semibold text-[#6E7A74] mb-2">Which documents?</p>
                        <div className="space-y-1.5">
                            {docs.map(x => (
                                <label key={x.id} className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid={`doc-pick-${x.id}`}>
                                    <input type="checkbox" checked={docSel.has(x.id)} onChange={() => toggleDoc(x.id)} className="accent-teal-dark" />
                                    <span className="text-[#1F2624]">{x.filename}</span>
                                    <span className="text-xs text-[#9CA6A1]">{x.date} · {x.category}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Share actions */}
            <div className="ml-card p-5 mt-4" data-testid="care-summary-share">
                <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => createShare(false)} disabled={busy === "link"} className="ml-btn-primary" data-testid="create-share-link">
                        {busy === "link" ? <Loader2 className="animate-spin" size={16} /> : <Share2 size={16} />} Create share link
                    </button>
                    <button onClick={openPdf} disabled={busy === "pdf"} className="ml-btn-secondary" data-testid="care-summary-download">
                        {busy === "pdf" ? <Loader2 className="animate-spin" size={16} /> : <Printer size={16} />} PDF / Print
                    </button>
                </div>
                <div className="flex flex-wrap items-end gap-2 mt-3">
                    <label className="flex-1 min-w-[220px]">
                        <span className="text-xs text-[#6E7A74]">Email it to the care team</span>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="doctor@clinic.com"
                            data-testid="share-email-input" className="mt-1 w-full rounded-lg border border-cream-dark px-3 py-2 text-sm" />
                    </label>
                    <button onClick={() => createShare(true)} disabled={busy === "email"} className="ml-btn-secondary" data-testid="send-share-email">
                        {busy === "email" ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />} Send
                    </button>
                </div>
                {share?.url && (
                    <div className="mt-3 flex items-center gap-2 rounded-xl bg-cream-light px-3 py-2" data-testid="share-result">
                        <input readOnly value={share.url} className="flex-1 bg-transparent text-sm text-[#2A3330]" />
                        <button onClick={copyLink} className="text-sm font-semibold text-teal-dark" data-testid="copy-share-link">Copy</button>
                    </div>
                )}
                <p className="mt-2 text-xs text-[#6E7A74]">Links are secure and expire in 30 days. Recipients see only what you picked. Need to write questions first? <Link to="/journey/appointments" className="underline text-teal-dark">Visit prep →</Link></p>
            </div>

            {/* Live preview of exactly what will be shared */}
            <h2 className="font-heading text-lg font-semibold text-[#1F2624] mt-8 mb-3">Preview — this is what they&apos;ll see</h2>
            <SharePreview data={d} sections={sel} docSel={docSel} range={range} shareNotes={sel.has("notes")} />

            <footer className="mt-10 pt-5 border-t border-cream-dark text-xs text-[#6E7A74]">
                Memory Lane is a caregiver-support tool, not a medical record.
                This summary combines what you&apos;ve entered and approved, to reduce repeating the story between caregivers and providers.
            </footer>
            </div>
        </Shell>
    );
}

// Live preview — composes exactly the sections the caregiver checked, in the
// same order the recipient sees them on the /s/:token page and in the PDF.
export function SharePreview({ data, sections, docSel, range, shareNotes }) {
    const d = data || {};
    const profile = d.patient_profile || {};
    const meds = (d.medication_changes?.medications?.length ? d.medication_changes.medications : profile.medications) || [];
    const journey = d.journey || {};
    if (sections.size === 0) {
        return (
            <div className="ml-card p-6 text-sm text-[#6E7A74]" data-testid="care-summary-preview-empty">
                Nothing selected — check at least one section above to see the preview.
            </div>
        );
    }
    const docJourney = { ...journey, documents: (journey.documents || []).filter(x => docSel.has(x.id)) };
    const rangeLabel = range !== "all" ? (RANGES.find(r => r.key === range) || {}).label : null;
    return (
        <div className="space-y-4" data-testid="care-summary-preview">
            {sections.has("patient_info") && <PatientInfo profile={profile} name={d.loved_one_name} />}
            {sections.has("diagnoses") && <JourneyRecord journey={journey} lovedOne={d.loved_one_name} only="diagnoses" brief={d.brief} />}
            {sections.has("medications") && <Medications meds={meds} concerns={d.appointment?.medication_concerns} />}
            {sections.has("care_team") && <HealthcareTeam team={profile.healthcare_team} services={d.services} />}
            {sections.has("timeline") && <JourneyRecord journey={filterEventsByRange(journey, range)} lovedOne={d.loved_one_name} only="timeline" range={rangeLabel} />}
            {sections.has("behaviors") && <BehaviorTimeline behaviors={d.behaviors || []} />}
            {sections.has("documents") && <JourneyRecord journey={docJourney} lovedOne={d.loved_one_name} only="documents" />}
            {sections.has("questions") && <QuestionsBlock appt={d.appointment} />}
            {shareNotes && <NotesBlock appt={d.appointment} />}
            {shareNotes && <CaregiverNotes notes={profile.notes} />}
        </div>
    );
}

function filterEventsByRange(journey, range) {
    const months = { "12m": 12, "6m": 6, "3m": 3, "1m": 1 }[range];
    if (!months) return journey;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - months);
    const iso = cutoff.toISOString().slice(0, 10);
    return { ...journey, events: (journey.events || []).filter(e => String(e.date || "") >= iso) };
}

function availableSections(d) {
    const profile = d.patient_profile || {};
    const j = d.journey || {};
    const appt = d.appointment || {};
    const insur = profile.insurance && Object.keys(profile.insurance).length;
    const meds = (d.medication_changes?.medications?.length ? d.medication_changes.medications : profile.medications) || [];
    const notesCount = (profile.notes ? 1 : 0) + (appt.medication_concerns || appt.recent_changes || appt.safety_concerns || appt.caregiver_concerns ? 1 : 0);
    return [
        { key: "patient_info", label: "Patient profile", hint: "insurance, allergies, contacts", count: (profile.demographics || insur || profile.allergies || profile.pharmacy) ? 1 : (Object.keys(d.intake || {}).length ? 1 : 0) },
        { key: "diagnoses", label: "Diagnoses & stage", hint: `${(j.diagnoses || []).length} on record`, count: (j.diagnoses || []).length + (j.stage ? 1 : 0) },
        { key: "medications", label: "Medications", hint: `${meds.length} listed`, count: meds.length },
        { key: "care_team", label: "Care team", hint: `${(profile.healthcare_team || []).length + (d.services || []).length} providers`, count: (profile.healthcare_team || []).length + (d.services || []).length },
        { key: "timeline", label: "Journey timeline", hint: `${(j.events || []).length} events, chronological`, count: (j.events || []).length },
        { key: "behaviors", label: "Recent behaviors", hint: `${(d.behaviors || []).length} logged`, count: (d.behaviors || []).length },
        { key: "documents", label: "Documents", hint: `${(j.documents || []).length} files`, count: (j.documents || []).length },
        { key: "questions", label: "Questions for the visit", hint: `${(appt.questions || []).length} written`, count: (appt.questions || []).length },
        { key: "notes", label: "Caregiver notes & concerns", hint: "your observations", count: notesCount },
    ];
}

function PatientInfo({ profile, name }) {
    const dem = profile.demographics || {};
    const insur = profile.insurance || {};
    const ph = profile.pharmacy || {};
    const ec = profile.emergency_contacts || [];
    const allerg = typeof profile.allergies === "object" ? Object.values(profile.allergies).filter(Boolean).join(", ") : profile.allergies;
    const row = (label, val) => val ? <p className="text-sm text-[#1F2624]"><strong>{label}:</strong> {val}</p> : null;
    return (
        <Section icon={User} title={`${name ? `${name.split(" ")[0]}'s` : "Patient"} profile`} testid="care-summary-patient-info">
            {row("Name", dem.full_name || name)}
            {row("Date of birth", dem.dob || dem.date_of_birth)}
            {row("Allergies", allerg)}
            {row("Insurance", [insur.provider || insur.primary, insur.plan || insur.secondary, insur.member_id].filter(Boolean).join(" · "))}
            {row("Pharmacy", [ph.name, ph.phone].filter(Boolean).join(" · "))}
            {ec.length > 0 && <p className="text-sm text-[#1F2624]"><strong>Emergency:</strong> {ec.map(c => `${c.name || ""} ${c.phone || ""}`.trim()).join("; ")}</p>}
            {!(dem.full_name || allerg || insur.provider || ph.name || ec.length) && <p className="text-sm text-[#6E7A74]">Add insurance, allergies, and contacts in the patient profile so providers have the basics.</p>}
        </Section>
    );
}

function QuestionsBlock({ appt }) {
    const qs = appt?.questions || [];
    if (!qs.length) return null;
    return (
        <Section icon={ClipboardList} title="Questions for this visit" testid="care-summary-questions">
            <ul className="list-disc list-inside text-sm text-[#1F2624] space-y-1">{qs.map((q, i) => <li key={i}>{q}</li>)}</ul>
        </Section>
    );
}

function NotesBlock({ appt }) {
    if (!appt) return null;
    const items = [["Recent changes", appt.recent_changes], ["Safety concerns", appt.safety_concerns], ["Caregiver concerns", appt.caregiver_concerns]].filter(x => x[1]);
    if (!items.length) return null;
    return (
        <Section icon={MessageCircle} title="Caregiver observations" testid="care-summary-observations">
            {items.map(([l, v]) => <div key={l} className="mb-2"><p className="text-[10px] uppercase tracking-wider text-[#6E7A74]">{l}</p><p className="text-sm text-[#1F2624]">{v}</p></div>)}
        </Section>
    );
}


// -------- shell + sections --------

function Shell({ children }) {
    // Rendered inside Layout since Phase 2 (mobile hardening) — Layout's main
    // already provides page padding, so this only narrows + Clarity-masks.
    return (
        <div className="max-w-3xl mx-auto" data-clarity-mask="true">{children}</div>
    );
}

function Section({ icon: Icon, title, empty, emptyAction, children, testid }) {
    if (empty) {
        return (
            <section className="ml-card p-5 sm:p-6 opacity-90" data-testid={testid}>
                <SectionHeader icon={Icon} title={title} />
                <p className="text-sm text-[#6E7A74] mt-1">{empty}</p>
                {emptyAction}
            </section>
        );
    }
    return (
        <section className="ml-card p-5 sm:p-6" data-testid={testid}>
            <SectionHeader icon={Icon} title={title} />
            {children}
        </section>
    );
}

function SectionHeader({ icon: Icon, title }) {
    return (
        <div className="flex items-center gap-2 mb-3">
            <span className="rounded-full bg-teal-soft text-teal-dark p-1.5">
                <Icon size={14} strokeWidth={2} />
            </span>
            <h2 className="font-heading text-base font-semibold text-[#1F2624]">{title}</h2>
        </div>
    );
}

function Snapshot({ intake }) {
    const rows = [
        ["FAST stage", intake.fast_stage || intake.stage],
        ["Burnout (intake)", intake.burnout_score],
        ["Hours/week", intake.hours_per_week],
        ["Family support", intake.family_support],
        ["Living situation", intake.living_situation],
    ].filter(([, v]) => v !== undefined && v !== null && v !== "");

    return (
        <Section
            icon={User}
            title="Snapshot"
            empty={rows.length === 0 ? "Finish your intake to fill this in." : null}
            emptyAction={
                rows.length === 0 ? (
                    <Link to="/intake" className="ml-btn-secondary mt-3 inline-flex !min-h-[36px] !py-1.5 !px-3 text-sm">
                        Complete intake <ChevronRight size={14} strokeWidth={1.8} />
                    </Link>
                ) : null
            }
            testid="care-summary-snapshot"
        >
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                {rows.map(([k, v]) => (
                    <div key={k}>
                        <dt className="text-[10px] uppercase tracking-wider text-[#6E7A74]">{k}</dt>
                        <dd className="text-[#1F2624] font-medium mt-0.5">{String(v)}</dd>
                    </div>
                ))}
            </dl>
        </Section>
    );
}

function CaregiverNarrative({ story, concerns }) {
    const challenges = story.challenges;
    const empty = !challenges && !(concerns?.length);
    return (
        <Section
            icon={Heart}
            title="In your own words"
            empty={empty ? "Add your story so doctors hear it from you." : null}
            testid="care-summary-narrative"
        >
            {concerns?.length > 0 && (
                <>
                    <p className="text-[10px] uppercase tracking-wider text-[#6E7A74] mb-1">Top concerns</p>
                    <ul className="list-disc list-inside text-sm text-[#1F2624] space-y-0.5 mb-3">
                        {concerns.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                </>
            )}
            {challenges && (
                <blockquote className="border-l-2 border-cream-dark pl-3 text-sm text-[#4C5551] italic">
                    {typeof challenges === "string" ? challenges : challenges.join(" · ")}
                </blockquote>
            )}
        </Section>
    );
}

function BehaviorTimeline({ behaviors }) {
    return (
        <Section
            icon={Brain}
            title="Recent behaviors"
            empty={behaviors.length === 0 ? "No behaviors logged yet — the more you log, the more useful this becomes." : null}
            emptyAction={
                behaviors.length === 0 ? (
                    <Link to="/behavior" className="ml-btn-secondary mt-3 inline-flex !min-h-[36px] !py-1.5 !px-3 text-sm">
                        Log a behavior <ChevronRight size={14} strokeWidth={1.8} />
                    </Link>
                ) : null
            }
            testid="care-summary-behaviors"
        >
            <ul className="space-y-2 text-sm">
                {behaviors.slice(0, 8).map((b, i) => (
                    <li key={i} className="flex items-start gap-3 py-1.5 border-b border-cream-light last:border-0">
                        <span className="text-xs text-[#6E7A74] w-20 shrink-0 mt-0.5">{relativeTime(b.created_at)}</span>
                        <div className="flex-1 min-w-0">
                            <div className="font-medium text-[#1F2624]">{b.behavior_type}</div>
                            <div className="text-xs text-[#4C5551] mt-0.5">
                                {b.time_of_day && <span>{b.time_of_day} · </span>}
                                Severity {b.severity || "—"}
                                {b.what_helped && <> · helped: <em>{b.what_helped}</em></>}
                            </div>
                        </div>
                    </li>
                ))}
            </ul>
        </Section>
    );
}

function BurnoutTrend({ burnouts }) {
    if (!burnouts.length) return null;
    const latest = burnouts[0];
    const trend = [...burnouts].reverse().map((b) => b.score).join(" → ");
    return (
        <Section icon={Sparkles} title="Caregiver burnout trend" testid="care-summary-burnout">
            <div className="text-sm">
                <p className="text-[#1F2624]">
                    <strong>Latest:</strong>{" "}
                    <span className={latest.risk_level === "high" ? "text-coral-dark font-semibold" : "text-[#1F2624]"}>
                        {(latest.risk_level || "").toUpperCase()} risk
                    </span>
                    {latest.score !== undefined && <> · {latest.score}/30</>}
                    {" · "}{relativeTime(latest.created_at)}
                </p>
                {burnouts.length > 1 && (
                    <p className="text-xs text-[#6E7A74] mt-1">Trend (oldest → newest): {trend}</p>
                )}
                {latest.risk_level === "high" && (
                    <p className="mt-2 text-xs text-coral-dark bg-coral-light px-3 py-2 rounded-lg">
                        High-risk burnout — please consider respite, caregiver therapy, or hospice/PACE evaluation.
                    </p>
                )}
            </div>
        </Section>
    );
}

function Medications({ meds, concerns }) {
    const empty = !meds.length && !concerns;
    return (
        <Section
            icon={Pill}
            title="Medications & concerns"
            empty={empty ? "Add medications to your patient profile so providers see the full picture." : null}
            testid="care-summary-meds"
        >
            {meds.length > 0 && (
                <ul className="text-sm text-[#1F2624] space-y-1">
                    {meds.slice(0, 10).map((m, i) => (
                        <li key={i}>
                            <strong>{m.name || m.medication || m}</strong>
                            {(m.dose || m.dosage) && <> — {m.dose || m.dosage}</>}
                            {(m.schedule || m.frequency) && <> — {m.schedule || m.frequency}</>}
                        </li>
                    ))}
                </ul>
            )}
            {concerns && (
                <p className="mt-3 text-sm text-[#4C5551] bg-cream-light rounded-lg px-3 py-2">
                    <strong className="text-[#1F2624]">Caregiver observation:</strong> {concerns}
                </p>
            )}
        </Section>
    );
}

function JourneyRecord({ journey, lovedOne, only, brief, range }) {
    if (!journey) return null;
    const first = (lovedOne || "").split(" ")[0];
    const events = [...(journey.events || [])].filter(e => e.label).sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
    const docs = (journey.documents || []);
    if (only === "diagnoses") {
        const dx = journey.diagnoses || [];
        if (!dx.length && !journey.stage && !brief?.stage) return null;
        return (
            <Section icon={FileCheck2} title="Diagnoses & stage" testid="care-summary-diagnoses">
                {dx.length > 0 && <p className="text-sm text-[#1F2624]"><strong>Documented diagnoses:</strong> {dx.join(", ")}</p>}
                {journey.stage?.label && <p className="text-sm text-[#1F2624] mt-1"><strong>Stage:</strong> {journey.stage.label}{journey.stage.evidence ? ` — ${journey.stage.evidence}` : ""}</p>}
                {!journey.stage?.label && brief?.stage && <p className="text-sm text-[#1F2624] mt-1"><strong>Stage:</strong> {brief.stage}</p>}
                <p className="text-xs text-[#6E7A74] mt-2">Document-verified from uploads you approved.</p>
            </Section>
        );
    }
    if (only === "timeline") {
        if (!events.length) return null;
        return (
            <Section icon={FileCheck2} title={`${first ? `${first}'s` : "Care"} timeline${range && range !== "all" ? ` · ${range}` : ""}`} testid="care-summary-timeline">
                <div className="space-y-1">{events.map(e => <p key={e.id} className="text-sm text-[#2A3330]"><strong>{e.date}</strong> — {e.label}</p>)}</div>
            </Section>
        );
    }
    if (only === "documents") {
        if (!docs.length) return null;
        return (
            <Section icon={FileCheck2} title="Documents" testid="care-summary-documents">
                {docs.map(x => <p key={x.id} className="text-sm text-[#2A3330] mt-1">• <strong>{x.date}</strong> — {x.filename} <span className="text-xs text-[#9CA6A1]">({x.category})</span></p>)}
                <p className="text-xs text-[#6E7A74] mt-2">Originals stay in {first ? `${first}'s` : "the"} Journey; recipients see this list.</p>
            </Section>
        );
    }
    return null;
}

function HealthcareTeam({ team, services }) {
    const svc = services || [];
    const all = [
        ...(team || []).map(p => ({ name: p.name, role: p.role || p.specialty, phone: p.phone })),
        ...svc.map(p => ({ name: p.name, role: p.specialty || p.role, phone: p.phone })),
    ].filter(p => p.name);
    if (!all.length) return null;
    return (
        <Section icon={Stethoscope} title="Care team" testid="care-summary-team">
            <ul className="text-sm text-[#1F2624] space-y-1">
                {all.slice(0, 12).map((p, i) => (
                    <li key={i}><strong>{p.name}</strong>{p.role && <> · {p.role}</>}{p.phone && <> · {p.phone}</>}</li>
                ))}
            </ul>
        </Section>
    );
}

function RoadmapProgress({ roadmap }) {
    const keys = ["early", "middle", "late"].filter((k) => Array.isArray(roadmap[k]) && roadmap[k].length);
    if (!keys.length) return null;
    return (
        <Section icon={ClipboardList} title="Roadmap progress" testid="care-summary-roadmap">
            <ul className="text-sm text-[#1F2624] space-y-1">
                {keys.map((k) => (
                    <li key={k}>
                        <strong>{k.charAt(0).toUpperCase() + k.slice(1)} stage</strong> — {roadmap[k].length} items completed
                    </li>
                ))}
            </ul>
        </Section>
    );
}

function GuideProgress({ guide }) {
    const verdict = guide.score || guide.verdict || guide.result;
    const answered = guide.answers ? Object.keys(guide.answers).length : 0;
    if (!verdict && !answered) return null;
    return (
        <Section icon={FileCheck2} title="GUIDE program progress" testid="care-summary-guide">
            <div className="text-sm text-[#1F2624]">
                {verdict && <p><strong>Eligibility status:</strong> {String(verdict)}</p>}
                {answered > 0 && <p className="text-xs text-[#6E7A74] mt-1">{answered} screener question(s) answered</p>}
            </div>
        </Section>
    );
}

function AppointmentPrep({ appt }) {
    if (!appt) return null;
    return (
        <Section icon={ClipboardList} title="Upcoming visit prep" testid="care-summary-appt">
            <div className="text-sm text-[#1F2624] space-y-2">
                {(appt.provider_name || appt.appointment_date) && (
                    <p className="text-xs text-[#6E7A74]">
                        {appt.provider_name && <>Provider: <strong className="text-[#1F2624]">{appt.provider_name}</strong></>}
                        {appt.appointment_date && <> · Date: <strong className="text-[#1F2624]">{appt.appointment_date}</strong></>}
                    </p>
                )}
                {appt.recent_changes && (
                    <div>
                        <p className="text-[10px] uppercase tracking-wider text-[#6E7A74]">Recent changes</p>
                        <p>{appt.recent_changes}</p>
                    </div>
                )}
                {appt.safety_concerns && (
                    <div>
                        <p className="text-[10px] uppercase tracking-wider text-[#6E7A74]">Safety concerns</p>
                        <p>{appt.safety_concerns}</p>
                    </div>
                )}
                {appt.caregiver_concerns && (
                    <div>
                        <p className="text-[10px] uppercase tracking-wider text-[#6E7A74]">Caregiver concerns</p>
                        <p>{appt.caregiver_concerns}</p>
                    </div>
                )}
                {appt.questions?.length > 0 && (
                    <div>
                        <p className="text-[10px] uppercase tracking-wider text-[#6E7A74]">Questions for the care team</p>
                        <ul className="list-disc list-inside">
                            {appt.questions.map((q, i) => <li key={i}>{q}</li>)}
                        </ul>
                    </div>
                )}
            </div>
        </Section>
    );
}

function LaneQA({ qa }) {
    if (!qa.length) return null;
    return (
        <Section icon={MessageCircle} title="Recent questions Lane was asked" testid="care-summary-lane">
            <ul className="space-y-3 text-sm">
                {qa.map((item, i) => (
                    <li key={i}>
                        <p className="text-[10px] uppercase tracking-wider text-[#6E7A74]">
                            Caregiver · {relativeTime(item.at)}
                        </p>
                        <p className="text-[#1F2624]">{item.question}</p>
                        <p className="text-[10px] uppercase tracking-wider text-[#6E7A74] mt-1">Lane</p>
                        <p className="text-[#4C5551] italic line-clamp-3">{item.answer}</p>
                    </li>
                ))}
            </ul>
        </Section>
    );
}

function CaregiverNotes({ notes }) {
    if (!notes) return null;
    return (
        <Section icon={FileText} title="Additional notes" testid="care-summary-notes">
            <p className="text-sm text-[#1F2624] whitespace-pre-wrap">{notes}</p>
        </Section>
    );
}

function relativeTime(iso) {
    if (!iso) return "";
    const ts = new Date(iso);
    const delta = Date.now() - ts.getTime();
    if (delta < 3600_000) return `${Math.max(1, Math.floor(delta / 60000))}m ago`;
    if (delta < 86400_000) return `${Math.floor(delta / 3600_000)}h ago`;
    const d = Math.floor(delta / 86400_000);
    if (d === 0) return "today";
    if (d === 1) return "yesterday";
    if (d < 30) return `${d}d ago`;
    if (d < 365) return `${Math.floor(d / 30)}mo ago`;
    return `${Math.floor(d / 365)}y ago`;
}
